import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, TextField, PrimaryButton, Spinner, SpinnerSize, Dropdown, IDropdownOption } from "@fluentui/react";
import { getAddNGWordData, addNGWord } from "../../../api/admin/api_admin";
import { AddNGWordResponse } from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog, LinkButton } from "../components";

export const AddNGWord = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<AddNGWordResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Form states
    const [ngWord, setNGWord] = useState<string>("");
    const [ngLevel, setNGLevel] = useState<string>("1");
    const [groupCd, setGroupCd] = useState<string>("0");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getAddNGWordData();
            if ("error" in result) {
                setError(result.error);
            } else {
                setData(result);
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);

        // バリデーション
        if (!ngWord) {
            setError("禁止ワードは必須です");
            setSubmitting(false);
            return;
        }
        if (ngWord.includes("'")) {
            setError("禁止ワードにシングルクォーテーションは使用できません");
            setSubmitting(false);
            return;
        }

        try {
            const result = await addNGWord({
                ng_word: ngWord,
                ng_level: ngLevel,
                group_cd: groupCd
            });

            if (result.success) {
                navigate("/menu/ngwordslist");
            } else {
                setError(result.message || "登録に失敗しました");
            }
        } catch (err) {
            setError("登録中にエラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    const getNGLevelOptions = (): IDropdownOption[] => {
        return [
            { key: '1', text: '1 : 警告表示' },
            { key: '2', text: '2 : 生成不可' }
        ];
    };

    const getGroupOptions = (): IDropdownOption[] => {
        if (!data) return [];
        return [
            { key: '0', text: '' },
            ...data.group_list.map(group => ({
                key: group.GRP_group_cd,
                text: `${group.GRP_group_cd} : ${group.GRP_group_name}`
            }))
        ];
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="禁止ワード登録" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { maxWidth: 600 } }}>
                <TextField
                    name="ng_word"
                    label="禁止ワード"
                    value={ngWord}
                    onChange={(_, newValue) => setNGWord(newValue || "")}
                    required
                    maxLength={100}
                    description="※シングルクォーテーションは使用できません。"
                    autoComplete="off"
                />

                <Dropdown
                    label="禁止レベル"
                    options={getNGLevelOptions()}
                    selectedKey={ngLevel}
                    onChange={(_, option) => setNGLevel((option?.key as string) || "1")}
                />

                <Dropdown
                    label="グループCD"
                    options={getGroupOptions()}
                    selectedKey={groupCd}
                    onChange={(_, option) => setGroupCd((option?.key as string) || "0")}
                />

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        text="登録"
                        onClick={handleSubmit}
                        disabled={submitting || !ngWord}
                    />
                    <LinkButton link_to="/menu/ngwordslist" text="戻る" />
                </Stack>
            </Stack>
        </Stack>
    );
}